import { useTranslation } from 'react-i18next'

import AddIcon from '@/assets/icons/add.svg?react'
import Button from '@/components/Button/Button'
import type { SelectOption } from '@/components/Select/Select'
import Filters, {
  type Filter,
  type FiltersProps
} from '@/components/common/Filters/Filters'
import { checkIfFilterSelected } from '@/utils/check-if-filters-selected'

import type {
  SectionListFilters,
  SectionListFiltersKey
} from '../constants/section-list-filters'
import { coursesQueryFilterOptions } from '../queries/sectionsListFilters/coursesQueryFilterOptions'
import { curriculumsQueryFilterOptions } from '../queries/sectionsListFilters/curriculumsQueryFilterOptions'
import { sectionsQueryFilterOptions } from '../queries/sectionsListFilters/sectionsQueryFilterOptions'
import { getSectionStatusOptions } from '../utils/get-section-status-options'

type SectionsListFiltersProps = {
  filters: Omit<SectionListFilters, 'page' | 'pageSize'>
  onFilterChange: (
    key: SectionListFiltersKey,
    value?: SelectOption<string>[] | string[] | Date
  ) => void
} & Pick<FiltersProps, 'onClearAll' | 'manageColumns'>

const SectionsListFilters = (props: SectionsListFiltersProps) => {
  const { t } = useTranslation('sections')

  const filters: Filter[] = [
    {
      label: t('label.name'),
      variant: 'async-multiselect',
      filterProps: {
        id: 'section-filter',
        multiple: true,
        queryOptions: sectionsQueryFilterOptions,
        value: props.filters.ids,
        placeholder: t('label.name'),
        onChange: value => props.onFilterChange('ids', value)
      }
    },
    {
      label: t('label.course'),
      variant: 'async-multiselect',
      filterProps: {
        id: 'course-filter',
        multiple: true,
        queryOptions: coursesQueryFilterOptions,
        value: props.filters.courseIds,
        placeholder: t('label.course'),
        onChange: value => props.onFilterChange('courseIds', value)
      }
    },
    {
      label: t('label.curriculum'),
      variant: 'async-multiselect',
      filterProps: {
        id: 'curriculum-filter',
        multiple: true,
        queryOptions: curriculumsQueryFilterOptions,
        value: props.filters.curriculumIds,
        placeholder: t('label.curriculum'),
        onChange: value => props.onFilterChange('curriculumIds', value)
      }
    },
    {
      label: t('label.status'),
      variant: 'multiselect',
      filterProps: {
        id: 'status-filter',
        multiple: true,
        options: getSectionStatusOptions(t),
        value: props.filters.statuses,
        placeholder: t('label.status'),
        onChange: value => props.onFilterChange('statuses', value)
      }
    }
  ]

  const isFilterSelected = checkIfFilterSelected(filters)

  return (
    <Filters
      filters={filters}
      disabledClearAllButton={!isFilterSelected}
      onClearAll={props.onClearAll}
      manageColumns={props.manageColumns}
      actionsRender={
        <Button asLink to="/sections/add" icon={<AddIcon />}>
          {t('button.add-new-section')}
        </Button>
      }
    />
  )
}

export default SectionsListFilters
